import { useRef } from "react";
import { UseFormSetError } from "react-hook-form";

import {
  useIntegrationConfiguration,
  useIntegrationConfigurationSchema,
  useUpdateIntegrationConfiguration,
} from "../hooks/useIntegrationConfiguration";
import type { Integration } from "../types";
import { ConfigurationDialog } from "./ConfigurationDialog";

import type { ConfigurationContext } from "@/types/configuration";

interface IntegrationConfigurationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  integration: Integration;
  context: ConfigurationContext;
}

export function IntegrationConfigurationDialog({
  open,
  onOpenChange,
  integration,
  context,
}: IntegrationConfigurationDialogProps) {
  const schemaQuery = useIntegrationConfigurationSchema(
    integration.name,
    context,
    open
  );

  const configQuery = useIntegrationConfiguration(
    integration.name,
    context,
    open
  );

  // Store form methods refs to pass to hook
  type FormValues = Record<string, string | { id: string; values: Record<string, string>; }[]>;
  const formMethodsRef = useRef<{
    setError?: UseFormSetError<FormValues>;
    getValues?: () => FormValues;
      }>({});

  const updateMutation = useUpdateIntegrationConfiguration<FormValues>(context, {
    getFormMethods: () => ({
      setError: formMethodsRef.current.setError,
      getValues: formMethodsRef.current.getValues,
    }),
  });

  const handleSave = async (configurations: Record<string, string>, isActive: boolean) => {
    await updateMutation.mutateAsync({
      integrationName: integration.name,
      configurations,
      isActive,
      currentIsActive: integration.isActive,
    });
  };

  return (
    <ConfigurationDialog
      open={open}
      onOpenChange={onOpenChange}
      title={integration.displayName}
      description={integration.description}
      isActive={integration.isActive}
      schemaQuery={schemaQuery}
      configQuery={configQuery}
      onSave={handleSave}
      isLoading={updateMutation.isPending}
      context={context}
      showEnableToggle
      onFormMethodsReady={(setError, getValues) => {
        formMethodsRef.current.setError = setError;
        formMethodsRef.current.getValues = getValues as () => FormValues;
      }}
    />
  );
}
